import type { CgtRuleSet } from "./types.js";

// CGT ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// The CGT *mechanics* (50% discount, the both-endpoints-excluded 12-month test,
// losses-before-discount ordering, the five-element cost base) are stable across
// FYs; the part that reprices is the MARGINAL SCALE that taxes the net gain —
// and that lives in the shared `income-tax` engine's own fyXXXX-XX.ts (16% second
// bracket for FY25-26, dropping to 15% for FY26-27, 14% for FY27-28). So a new
// CGT FY only needs a fresh ruleset when a CGT-specific figure changes.
//
// FORWARD-LOOKING (out of scope for this FY, flagged for the build team): from
// 1 July 2027 the 50% discount is announced (not yet law) to be replaced by
// cost-base indexation + a 30% MINIMUM TAX (a floor, NOT a cap) on net capital
// gains for crypto and some other assets. The FY2027-28 ruleset must carry that
// change — never edit this shipped FY25-26 ruleset to model it.
//
// Verified figures (see docs/research/discovery/verification/cgt-*-figures.md):
//   - 50% CGT discount for resident individuals holding ≥12 months   [CITED]
//   - 12-month test EXCLUDES the acquisition day and the CGT-event day,
//     so the asset must be held a minimum of 367 calendar days (368 in a leap
//     year) → daysHeld (both endpoints excluded) must exceed 365             [CITED]
//   - net capital gain taxed at the marginal rate on top of other income,
//     no separate/flat CGT rate                                              [CITED]
//   - losses applied to the GROSS gain BEFORE the discount; offset capital
//     gains only; carry forward indefinitely                                 [CITED]
export const fy2025_26: CgtRuleSet = {
  engine: "cgt",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  sources: {
    cgtDiscount:
      "https://www.ato.gov.au/individuals-and-families/investments-and-assets/capital-gains-tax/cgt-discount",
    howToCalculate:
      "https://www.ato.gov.au/individuals-and-families/investments-and-assets/capital-gains-tax/calculating-your-cgt/how-to-calculate-your-cgt",
    costBase:
      "https://www.ato.gov.au/individuals-and-families/investments-and-assets/capital-gains-tax/calculating-your-cgt/cost-base-of-asset",
    usingCapitalLosses:
      "https://www.ato.gov.au/individuals-and-families/investments-and-assets/capital-gains-tax/calculating-your-cgt/using-capital-losses-to-reduce-capital-gains",
    washSaleTA20087:
      "https://www.ato.gov.au/law/view/print?DocID=TPA%2FTA20087%2FNAT%2FATO%2F00001",
    washSaleMedia:
      "https://www.ato.gov.au/media-centre/wash-sales-the-ato-is-cleaning-up-dirty-laundry",
    marginalRates:
      "https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents",
  },
  sourcesVerifiedAt: "2026-06-22",
  // 50% discount for resident individuals.
  discountRate: 0.5,
  // >12 months, both endpoints excluded → daysHeld must EXCEED 365.
  minDaysHeldForDiscount: 365,
};
